import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GetLobbyUsers, CreateLobbyUser, UpdateLobbyUser, DeleteLobbyUser } from '../../../api';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
  { value: 'readonly', label: 'Read Only' },
];

const ROLE_COLORS = { admin: 'error', manager: 'warning', staff: 'primary', readonly: 'default' };

const UserDialog = ({ open, initial, onSave, onClose }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({ name: '', email: '', lobbyRole: 'staff', password: '' });
  useEffect(() => { if (initial) setForm({ password: '', ...initial }); }, [initial]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? 'Edit User' : 'Add User'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Name" value={form.name} onChange={e => set('name', e.target.value)} fullWidth />
          <TextField label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} fullWidth disabled={isEdit} />
          {!isEdit && <TextField label="Password" type="password" value={form.password} onChange={e => set('password', e.target.value)} fullWidth />}
          <TextField select label="Role" value={form.lobbyRole} onChange={e => set('lobbyRole', e.target.value)} fullWidth>
            {ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const Users = ({ access }) => {
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = () => GetLobbyUsers(access?.org?.id).then(setUsers).catch(() => {});
  useEffect(() => { load(); }, [access]); // eslint-disable-line

  const handleSave = async form => {
    if (form.id) await UpdateLobbyUser(form.id, access?.org?.id, { name: form.name, lobbyRole: form.lobbyRole });
    else await CreateLobbyUser({ ...form, org: access?.org?.id });
    setDialog(null);
    load();
  };

  const handleDelete = async id => { await DeleteLobbyUser(id, access?.org?.id); load(); };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Users</Typography>
        <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setDialog({})}>Add User</Button>
      </Box>
      <Table size="small" component={Paper}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(u => (
            <TableRow key={u.id}>
              <TableCell>{u.name}</TableCell>
              <TableCell>{u.email}</TableCell>
              <TableCell>
                <Chip label={ROLES.find(r => r.value === u.lobbyRole)?.label || u.lobbyRole} size="small" color={ROLE_COLORS[u.lobbyRole] || 'default'} />
              </TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => setDialog(u)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => handleDelete(u.id)}><DeleteIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!users.length && <TableRow><TableCell colSpan={4} align="center">No users</TableCell></TableRow>}
        </TableBody>
      </Table>
      <UserDialog open={!!dialog} initial={dialog} onSave={handleSave} onClose={() => setDialog(null)} />
    </Box>
  );
};

export default Users;
